import React, { useRef, useEffect } from 'react';
import { getContributorResume } from 'react-scale/corp/lib/api/growth';
import { Icon, Spinner } from '@scale/scaleui';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { logResumeSearchEvent } from './api';
import { ResumeSearchEventType } from '@scale/shared-types';
import { useUser } from 'react-scale/components/ui/UserContext';

type DownloadResumeButtonProps = {
  userId: string;
};

export const DownloadResumeButton = ({ userId }: DownloadResumeButtonProps) => {
  const requestingUser = useUser();
  const ref = useRef<HTMLAnchorElement>(null);

  const { isFetching, refetch, data } = useQuery(
    ['getContributorResumeDownload', userId],
    async () => {
      return await getContributorResume(userId);
    },
    {
      onError: async () => toast.error(`Failed to download resume.`),
      enabled: false,
    },
  );

  const { mutate: logEvent } = useMutation(
    async () =>
      await logResumeSearchEvent({
        type: ResumeSearchEventType.Download,
        userId: requestingUser.id,
        data: { resumeOwner: userId },
      }),
  );

  const onClick = () => {
    logEvent();
    refetch();
  };

  useEffect(() => {
    if (data?.resumeUrl) {
      ref?.current?.click();
    }
  }, [data]);

  return (
    <>
      <a href={data?.resumeUrl} ref={ref} />
      {isFetching ? (
        <Spinner size="xs" variant="neutral" />
      ) : (
        <Icon
          icon={['fas', 'download']}
          onClick={onClick}
          rounded
          size="sm"
          tooltip="Download"
          variant="neutral"
        />
      )}
    </>
  );
};
